import { getChartPalette } from "./chartPalette";
import { estimateBarSpacing } from "./chartOverlayCanvas";

export const SESSION_OVERLAY_EMPTY = { bands: [], dividers: [] };

const SESSION_PREMARKET = "premarket";
const SESSION_REGULAR = "regular";
const SESSION_AFTERHOURS = "afterhours";
const US_EQUITY_MARKET_TIME_ZONE = "America/New_York";

export const INTRADAY_SESSION_SCALES = new Set([
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
]);

const US_EQUITY_PREMARKET_START_MINUTES = 4 * 60;
const US_EQUITY_REGULAR_START_MINUTES = 9 * 60 + 30;
const US_EQUITY_REGULAR_END_MINUTES = 16 * 60;
const US_EQUITY_AFTERHOURS_END_MINUTES = 20 * 60;
const zonedDateTimeFormatterCache = new Map();

const normalizeAssetType = (assetType) =>
  String(assetType || "")
    .trim()
    .toLowerCase();

const shouldShowUsEquitySessions = ({ assetType, scale }) =>
  normalizeAssetType(assetType) === "stocks" &&
  INTRADAY_SESSION_SCALES.has(scale);

const getZonedDateTimeFormatter = (timeZone) => {
  if (!zonedDateTimeFormatterCache.has(timeZone)) {
    zonedDateTimeFormatterCache.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  }

  return zonedDateTimeFormatterCache.get(timeZone);
};

const getZonedDateTimeParts = (absoluteTime, timeZone) => {
  if (!Number.isFinite(absoluteTime)) return null;

  const date = new Date(absoluteTime * 1000);
  if (Number.isNaN(date.getTime())) return null;

  const parts = getZonedDateTimeFormatter(timeZone).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const weekday = get("weekday");
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));

  if (
    !weekday ||
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  return {
    weekday,
    year,
    month,
    day,
    hour,
    minute,
  };
};

const getUsEquitySessionType = (absoluteTime) => {
  const parts = getZonedDateTimeParts(
    absoluteTime,
    US_EQUITY_MARKET_TIME_ZONE
  );
  if (!parts) return null;

  if (parts.weekday === "Sat" || parts.weekday === "Sun") return null;

  const minutes = parts.hour * 60 + parts.minute;
  if (
    minutes < US_EQUITY_PREMARKET_START_MINUTES ||
    minutes >= US_EQUITY_AFTERHOURS_END_MINUTES
  ) {
    return null;
  }
  if (minutes < US_EQUITY_REGULAR_START_MINUTES) {
    return SESSION_PREMARKET;
  }
  if (minutes < US_EQUITY_REGULAR_END_MINUTES) {
    return SESSION_REGULAR;
  }
  return SESSION_AFTERHOURS;
};

const getSessionDayKey = (absoluteTime) => {
  const parts = getZonedDateTimeParts(
    absoluteTime,
    US_EQUITY_MARKET_TIME_ZONE
  );
  if (!parts) return null;

  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(
    parts.day
  ).padStart(2, "0")}`;
};

export const buildSessionOverlayConfig = ({ candleData, assetType, scale }) => {
  if (
    !shouldShowUsEquitySessions({ assetType, scale }) ||
    !Array.isArray(candleData) ||
    candleData.length === 0
  ) {
    return SESSION_OVERLAY_EMPTY;
  }

  const byDay = new Map();

  candleData.forEach((entry) => {
    const time = Number(entry?.time);
    const absoluteTime = Number(entry?.absoluteTime);
    if (!Number.isFinite(time) || !Number.isFinite(absoluteTime)) return;

    const sessionType = getUsEquitySessionType(absoluteTime);
    if (!sessionType) return;

    const dayKey = getSessionDayKey(absoluteTime);
    if (!dayKey) return;

    let daySessions = byDay.get(dayKey);
    if (!daySessions) {
      daySessions = {
        [SESSION_PREMARKET]: null,
        [SESSION_REGULAR]: null,
        [SESSION_AFTERHOURS]: null,
      };
      byDay.set(dayKey, daySessions);
    }

    if (!daySessions[sessionType]) {
      daySessions[sessionType] = { startTime: time, endTime: time };
      return;
    }

    daySessions[sessionType].startTime = Math.min(
      daySessions[sessionType].startTime,
      time
    );
    daySessions[sessionType].endTime = Math.max(
      daySessions[sessionType].endTime,
      time
    );
  });

  const bands = [];
  const dividers = [];

  byDay.forEach((daySessions) => {
    const premarket = daySessions[SESSION_PREMARKET];
    const regular = daySessions[SESSION_REGULAR];
    const afterhours = daySessions[SESSION_AFTERHOURS];

    if (premarket) {
      bands.push({ ...premarket, type: SESSION_PREMARKET });
    }
    if (afterhours) {
      bands.push({ ...afterhours, type: SESSION_AFTERHOURS });
    }
    if (regular && premarket) {
      dividers.push({ time: regular.startTime, kind: "market-open" });
    }
    if (afterhours) {
      dividers.push({ time: afterhours.startTime, kind: "market-close" });
    }
  });

  if (bands.length === 0 && dividers.length === 0) {
    return SESSION_OVERLAY_EMPTY;
  }

  return {
    bands: bands.sort((a, b) => a.startTime - b.startTime),
    dividers: dividers.sort((a, b) => a.time - b.time),
  };
};

export const drawSessionOverlay = ({ chart, overlay, context, width, height }) => {
  if (
    !chart ||
    !context ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    (!Array.isArray(overlay?.bands) && !Array.isArray(overlay?.dividers))
  ) {
    return;
  }

  const palette = getChartPalette();
  const timeScale = chart.timeScale?.();
  if (!timeScale) return;

  const barSpacing = estimateBarSpacing(chart, width);
  const halfBar = Math.max(barSpacing / 2, 3);

  (overlay?.bands || []).forEach((band) => {
    if (
      !band ||
      !Number.isFinite(band.startTime) ||
      !Number.isFinite(band.endTime)
    ) {
      return;
    }

    const startCoordinate = timeScale.timeToCoordinate?.(band.startTime);
    const endCoordinate = timeScale.timeToCoordinate?.(band.endTime);
    if (!Number.isFinite(startCoordinate) || !Number.isFinite(endCoordinate)) {
      return;
    }

    const left = Math.max(0, Math.min(startCoordinate, endCoordinate) - halfBar);
    const right = Math.min(width, Math.max(startCoordinate, endCoordinate) + halfBar);
    if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) {
      return;
    }

    context.save();
    context.fillStyle =
      band.type === SESSION_AFTERHOURS
        ? palette.afterhoursFill
        : palette.premarketFill;
    context.fillRect(left, 0, right - left, height);
    context.restore();
  });

  (overlay?.dividers || []).forEach((divider) => {
    if (!divider || !Number.isFinite(divider.time)) return;

    const x = timeScale.timeToCoordinate?.(divider.time);
    if (!Number.isFinite(x) || x < -2 || x > width + 2) return;

    context.save();
    context.strokeStyle = palette.sessionDivider;
    context.lineWidth = 1;
    context.setLineDash([4, 4]);
    context.beginPath();
    context.moveTo(Math.round(x) + 0.5, 0);
    context.lineTo(Math.round(x) + 0.5, height);
    context.stroke();
    context.restore();
  });
};
